import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { MyOrganization } from '@/features/organizations/types/organization'
import type { BillingProduct } from '@/features/system-settings/types/system-settings'

export interface Organization {
  id: string
  name: string
  /** The backend role's name, e.g. "Organization Admin"; empty when there's none. */
  role: string
  permissions: string[]
  /** The products it has access to right now. */
  subscribedProducts: BillingProduct[]
  canCreateMerchants: boolean
}

export interface WorkspaceAccount {
  id: string
  organizationId: string
  name: string
  /** The backend role's name, e.g. "Workspace Admin"; empty when there's none. */
  role: string
  permissions: string[]
}

const CURRENT_ORGANIZATION_KEY = 'moose:current-organization'
const CURRENT_ACCOUNT_KEY = 'moose:current-account'

/**
 * Organizations and workspaces come from `GET /organizations/my` (see
 * `useSyncMyOrganizations`); only the current selection is client state,
 * persisted like stores/ui.ts's sidebarCollapsed.
 */

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Storage unavailable: the selection lasts until reload.
  }
}

export const useWorkspaceStore = defineStore('workspace', () => {
  const organizations = ref<Organization[]>([])
  const accounts = ref<WorkspaceAccount[]>([])
  const isLoaded = ref(false)

  const currentOrganizationId = ref<string | null>(readStored(CURRENT_ORGANIZATION_KEY))
  const currentAccountId = ref<string | null>(readStored(CURRENT_ACCOUNT_KEY))

  /** Replaces the lists with the API's data and repairs a stale persisted selection. */
  function setOrganizations(items: MyOrganization[]): void {
    organizations.value = items.map((item) => ({
      id: String(item.id),
      name: item.name,
      role: item.role ?? '',
      permissions: item.permissions,
      subscribedProducts: item.subscribed_products,
      canCreateMerchants: item.can_create_merchants,
    }))
    accounts.value = items.flatMap((item) =>
      item.workspaces.map((workspace) => ({
        id: String(workspace.id),
        organizationId: String(item.id),
        name: workspace.name,
        role: workspace.role ?? '',
        permissions: workspace.permissions,
      })),
    )

    const organization =
      organizations.value.find((item) => item.id === currentOrganizationId.value) ??
      organizations.value[0]
    currentOrganizationId.value = organization?.id ?? null

    const account =
      accounts.value.find(
        (item) =>
          item.id === currentAccountId.value && item.organizationId === currentOrganizationId.value,
      ) ?? accounts.value.find((item) => item.organizationId === currentOrganizationId.value)
    currentAccountId.value = account?.id ?? null

    if (currentOrganizationId.value)
      writeStored(CURRENT_ORGANIZATION_KEY, currentOrganizationId.value)
    if (currentAccountId.value) writeStored(CURRENT_ACCOUNT_KEY, currentAccountId.value)
    isLoaded.value = true
  }

  const currentOrganization = computed(
    () => organizations.value.find((org) => org.id === currentOrganizationId.value) ?? null,
  )
  const currentAccount = computed(
    () => accounts.value.find((account) => account.id === currentAccountId.value) ?? null,
  )
  const accountsInCurrentOrganization = computed(() =>
    accounts.value.filter((account) => account.organizationId === currentOrganizationId.value),
  )

  /** Forgets the loaded lists (on sign-out) so the next user never sees them. */
  function reset(): void {
    organizations.value = []
    accounts.value = []
    isLoaded.value = false
  }

  function setCurrentOrganization(id: string): void {
    currentOrganizationId.value = id
    writeStored(CURRENT_ORGANIZATION_KEY, id)

    const firstAccount = accounts.value.find((account) => account.organizationId === id)
    if (firstAccount) setCurrentAccount(firstAccount.id)
    else currentAccountId.value = null
  }

  function setCurrentAccount(id: string): void {
    currentAccountId.value = id
    writeStored(CURRENT_ACCOUNT_KEY, id)
  }

  /**
   * Selects an organization and one of its workspaces (the given one, else its first). Returns
   * false — changing nothing — when either isn't in the loaded lists.
   */
  function switchTo(organizationId: string, workspaceId?: string): boolean {
    if (!organizations.value.some((org) => org.id === organizationId)) return false

    const account = workspaceId
      ? accounts.value.find(
          (item) => item.id === workspaceId && item.organizationId === organizationId,
        )
      : undefined
    if (workspaceId && !account) return false

    setCurrentOrganization(organizationId)
    if (account) setCurrentAccount(account.id)
    return true
  }

  return {
    organizations,
    accounts,
    isLoaded,
    currentOrganizationId,
    currentAccountId,
    currentOrganization,
    currentAccount,
    accountsInCurrentOrganization,
    setOrganizations,
    reset,
    setCurrentOrganization,
    setCurrentAccount,
    switchTo,
  }
})
